import React from 'react';
import './ModelsTile.scss';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { ALL_MODEL_DEFINITIONS } from '../../../../shared/helpers/MonitorTypeHelpers';
import { PRICING } from '../../../../shared/routes/path';
import { canShowModel, numberFormat } from '../../../../shared/utils/utils';

interface ModelsTileProps {
  /** The account's `features`. */
  features: string[];
}

/**
 * The fourth card of the usage row: the account's AI models against the whole catalogue (the Enterprise
 * set). Same frame, label and value as the usage tiles (their classes), but no icon and no progress bar;
 * under the value the `/projects` model stack — active models sit still, locked ones are greyed, lift on
 * hover like the list's circles and lead to `/pricing`. Real account data, not the mock.
 */
const ModelsTile: React.FC<ModelsTileProps> = ({ features }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const models = ALL_MODEL_DEFINITIONS.map((def) => ({
    ...def,
    active: canShowModel(def.modelKey, features),
  }));
  const active = models.filter((model) => model.active).length;

  return (
    <div className="usage-tiles__card models-tile">
      <div className="usage-tiles__label">{t('aiModels')}</div>
      <div className="usage-tiles__value">
        {numberFormat().format(active)}
        <span className="usage-tiles__max">/ {numberFormat().format(models.length)}</span>
        <span className="usage-tiles__unit">{t('agencyHubModelsUnit')}</span>
      </div>
      <div className="model-stack models-tile__stack">
        {models.map((model) => (
          <React.Fragment key={model.modelKey}>
            {model.active ? (
              <span
                id={`models-tile-${model.modelKey}`}
                className="model-stack__item models-tile__model--active"
              >
                <span className={`${model.icon} ico--md`} />
              </span>
            ) : (
              <button
                type="button"
                id={`models-tile-${model.modelKey}`}
                className="model-stack__item models-tile__model--locked"
                aria-label={t('agencyHubModelLocked', { model: t(model.nameKey) })}
                onClick={() => navigate(PRICING)}
              >
                <span className={`${model.icon} ico--md`} />
              </button>
            )}
            <CustomTooltip
              tooltipText={model.active ? model.nameKey : 'agencyHubModelLocked'}
              options={model.active ? undefined : { model: t(model.nameKey) }}
              targetRef={`models-tile-${model.modelKey}`}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default ModelsTile;
